/**
 * Company Expenses
 * -----------------
 * Internal expense register — date, amount, category, narration with
 * optional bill / payment-screenshot attachments.  Month selector on top,
 * per-category totals strip, Excel export of the full month.
 *
 * Permissions: any signed-in user can view; invoicing_staff+ can add;
 * admin can edit/delete (matches backend route guards).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { fmt, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Paperclip, Image, Download, X, Loader2, Search,
         Pencil, Trash2, Wallet, Check, Ban, Clock } from "lucide-react";
import { useAuth, canDo } from "@/context/AuthContext";
import { PettyCashTab } from "@/components/expenses/PettyCashTab";
import { YearlyExpenseReport } from "@/components/expenses/YearlyExpenseReport";
import { ExpenseFilesEditor } from "@/components/expenses/ExpenseFilesEditor";

const STATUS_STYLE = {
    approved: "bg-emerald-50 text-emerald-700",
    pending: "bg-amber-50 text-amber-700",
    rejected: "bg-rose-50 text-rose-700",
};
const TABS = [["register", "Register"], ["petty", "Petty cash"], ["yearly", "Yearly report"]];

const catColors = ["bg-emerald-100 text-emerald-800", "bg-blue-100 text-blue-800",
                   "bg-amber-100 text-amber-800", "bg-rose-100 text-rose-800",
                   "bg-indigo-100 text-indigo-800", "bg-teal-100 text-teal-800",
                   "bg-orange-100 text-orange-800", "bg-slate-200 text-slate-700"];
const catColor = (name) => {
    let h = 0;
    for (const ch of (name || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return catColors[h % catColors.length];
};

export default function ExpensesPage() {
    const [tab, setTab] = useState("register");
    return (
        <div data-testid="expenses-page">
            <h1 className="text-3xl hdb-heading font-black text-slate-900">Company Expenses</h1>
            <p className="text-slate-500 mt-1 text-sm">
                Record bills &amp; payment screenshots with narration — marketing, travel, gifting and more
            </p>
            <div className="flex gap-1 border-b border-slate-200 mt-4 mb-5">
                {TABS.map(([k, label]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`expenses-tab-${k}`}
                            className={`px-4 py-2 text-sm font-bold -mb-px border-b-2 transition-colors
                                        ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
                        {label}
                    </button>
                ))}
            </div>
            {tab === "register" && <ExpenseRegister/>}
            {tab === "petty" && <PettyCashTab/>}
            {tab === "yearly" && <YearlyExpenseReport/>}
        </div>
    );
}

function ExpenseRegister() {
    const { user } = useAuth();
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [category, setCategory] = useState("");
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [categories, setCategories] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);      // { mode: "add" } | { mode: "edit", row }

    const canAdd = canDo(user, "invoicing_staff");
    const isAdmin = canDo(user, "admin");

    const loadCats = () => api.get("/expenses/categories")
        .then((r) => setCategories(r.data.map((c) => c.name)))
        .catch(() => {});

    const reqId = useRef(0);
    const load = () => {
        const id = ++reqId.current;
        setLoading(true);
        api.get("/expenses", { params: { month, category, q, status } })
            .then((r) => { if (id === reqId.current) setData(r.data); })
            .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load expenses"))
            .finally(() => { if (id === reqId.current) setLoading(false); });
    };
    useEffect(() => { loadCats(); }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, category, q, status]);

    const review = async (row, next) => {
        let note = "";
        if (next === "rejected") {
            note = window.prompt("Reason for rejecting (optional)") ?? null;
            if (note === null) return;
        }
        try {
            await api.post(`/expenses/${row.id}/review`, { status: next, note });
            toast.success(next === "approved" ? "Expense approved" : "Expense rejected");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    };

    const del = async (row) => {
        if (!window.confirm(`Delete this expense — ${fmt(row.amount)} on ${row.expense_date}?`)) return;
        try {
            await api.delete(`/expenses/${row.id}`);
            toast.success("Expense deleted");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    const totals = data?.totals || { grand: 0, count: 0, by_category: {} };
    const byCat = totals.by_category || {};

    return (
        <div data-testid="expenses-register">
            <div className="flex items-end justify-end mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                           className="hdb-input hdb-mono !w-44" data-testid="expense-month"/>
                    <a href={`${API}/expenses/report.xlsx?month=${month}`}
                       className="hdb-btn hdb-btn-ghost" data-testid="expenses-export-xlsx"
                       title="Download this month's expense report as Excel">
                        <Download size={13}/> Excel
                    </a>
                    {canAdd && (
                        <button className="hdb-btn hdb-btn-primary" data-testid="expense-add-btn"
                                onClick={() => setModal({ mode: "add" })}>
                            <Plus size={13}/> Add expense
                        </button>
                    )}
                </div>
            </div>

            {/* Category totals strip */}
            <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="expense-category-totals">
                <div className="hdb-card px-4 py-2 flex items-center gap-2">
                    <Wallet size={14} className="text-slate-500"/>
                    <span className="hdb-num text-sm font-black text-slate-900">{fmt(totals.grand)}</span>
                    <span className="text-xs text-slate-500 uppercase tracking-wider">
                        {totals.count} approved this month
                    </span>
                </div>
                {data?.pending?.count > 0 && (
                    <button onClick={() => setStatus(status === "pending" ? "" : "pending")}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1 bg-amber-100 text-amber-800
                                        ${status === "pending" ? "ring-2 ring-amber-600" : ""}`}
                            data-testid="expense-pending-chip">
                        <Clock size={11}/> {data.pending.count} awaiting approval · {fmt(data.pending.total)}
                    </button>
                )}
                {Object.entries(byCat).map(([cat, v]) => (
                    <button key={cat} onClick={() => setCategory(category === cat ? "" : cat)}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors
                                        ${category === cat ? "ring-2 ring-slate-900 border-transparent" : "border-transparent"}
                                        ${catColor(cat)}`}
                            data-testid={`expense-cat-chip-${cat}`}>
                        {cat} · {fmt(v.total)}
                    </button>
                ))}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <select value={category} onChange={(e) => setCategory(e.target.value)}
                        className="hdb-input !w-52" data-testid="expense-category-filter">
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400"/>
                    <input value={q} onChange={(e) => setQ(e.target.value)}
                           placeholder="Search narration…"
                           className="hdb-input !pl-8 !w-64" data-testid="expense-search"/>
                </div>
                <select value={status} onChange={(e) => setStatus(e.target.value)}
                        className="hdb-input !w-40" data-testid="expense-status-filter">
                    <option value="">All statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                </select>
                {(category || q || status) && (
                    <button onClick={() => { setCategory(""); setQ(""); setStatus(""); }}
                            className="hdb-btn hdb-btn-ghost text-xs" data-testid="expense-clear-filters">
                        Clear
                    </button>
                )}
            </div>

            {/* List */}
            <div className="hdb-card overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-slate-400" data-testid="expenses-loading">
                        <Loader2 className="inline animate-spin mr-2" size={14}/> Loading…
                    </div>
                ) : !data || data.rows.length === 0 ? (
                    <div className="p-10 text-center text-slate-400" data-testid="expenses-empty">
                        No expenses in {month}{category ? ` for ${category}` : ""}.
                    </div>
                ) : (
                    <table className="hdb-table w-full text-sm">
                        <thead>
                            <tr>
                                <th className="text-left">Date</th>
                                <th className="text-left">Category</th>
                                <th className="text-left">Narration</th>
                                <th className="text-right">Amount</th>
                                <th className="text-left">Proof</th>
                                <th className="text-left">Status</th>
                                <th className="text-left">By</th>
                                {canAdd && <th className="text-right">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r, i) => (
                                <tr key={r.id} data-testid={`expense-row-${i}`}>
                                    <td className="hdb-mono whitespace-nowrap">{r.expense_date}</td>
                                    <td>
                                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${catColor(r.category)}`}
                                              data-testid={`expense-cat-${i}`}>
                                            {r.category}
                                        </span>
                                    </td>
                                    <td className="text-slate-700 max-w-md">
                                        {r.narration || <span className="text-slate-300">—</span>}
                                        {r.payment_mode === "cash" && (
                                            <span className="ml-2 text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded"
                                                  data-testid={`expense-cash-${i}`}>CASH</span>
                                        )}
                                    </td>
                                    <td className="hdb-num hdb-num-right font-bold">{fmt(r.amount)}</td>
                                    <td className="whitespace-nowrap">
                                        {(r.files || []).map((f, j) => (
                                            <a key={f.id} href={`${API}/expenses/${r.id}/files/${f.id}`} target="_blank" rel="noreferrer"
                                               className={`text-[10px] font-bold inline-flex items-center gap-1 px-1.5 py-0.5 rounded mr-1
                                                           ${f.kind === "bill" ? "text-blue-700 bg-blue-50" : "text-emerald-700 bg-emerald-50"}`}
                                               title={f.name} data-testid={`expense-${f.kind === "bill" ? "bill" : "shot"}-${i}${j ? `-${j}` : ""}`}>
                                                {f.kind === "bill" ? <Paperclip size={9}/> : <Image size={9}/>}
                                                {f.kind === "bill" ? "Bill" : "Screenshot"}
                                            </a>
                                        ))}
                                        {!(r.files || []).length && <span className="text-slate-300 text-xs">—</span>}
                                    </td>
                                    <td>
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_STYLE[r.status]}`}
                                              title={r.review_note || (r.reviewed_by ? `by ${r.reviewed_by}` : "")}
                                              data-testid={`expense-status-${i}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="text-xs text-slate-500">{r.created_by}</td>
                                    {canAdd && (
                                        <td className="text-right whitespace-nowrap">
                                            {isAdmin && r.status !== "approved" && (
                                                <button onClick={() => review(r, "approved")}
                                                        className="hdb-btn hdb-btn-ghost text-xs mr-1 text-emerald-700"
                                                        title="Approve" data-testid={`expense-approve-${i}`}>
                                                    <Check size={11}/>
                                                </button>
                                            )}
                                            {isAdmin && r.status === "pending" && (
                                                <button onClick={() => review(r, "rejected")}
                                                        className="hdb-btn hdb-btn-ghost text-xs mr-1 text-rose-700"
                                                        title="Reject" data-testid={`expense-reject-${i}`}>
                                                    <Ban size={11}/>
                                                </button>
                                            )}
                                            {!isAdmin && (
                                                <button onClick={() => setModal({ mode: "files", row: r })}
                                                        className="hdb-btn hdb-btn-ghost text-xs" title="Add bill / screenshot"
                                                        data-testid={`expense-attach-${i}`}>
                                                    <Paperclip size={11}/>
                                                </button>
                                            )}
                                            {isAdmin && <>
                                            <button onClick={() => setModal({ mode: "edit", row: r })}
                                                    className="hdb-btn hdb-btn-ghost text-xs mr-1"
                                                    data-testid={`expense-edit-${i}`}>
                                                <Pencil size={11}/>
                                            </button>
                                            <button onClick={() => del(r)}
                                                    className="hdb-btn hdb-btn-ghost text-xs text-red-700"
                                                    data-testid={`expense-delete-${i}`}>
                                                <Trash2 size={11}/>
                                            </button>
                                            </>}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modal && (
                <ExpenseModal mode={modal.mode} row={modal.row} categories={categories}
                              isAdmin={isAdmin} onFilesChanged={load}
                              onClose={() => setModal(null)}
                              onSaved={() => { setModal(null); loadCats(); load(); }} />
            )}
        </div>
    );
}


function ExpenseModal({ mode, row, categories, isAdmin, onFilesChanged, onClose, onSaved }) {
    if (mode === "files") return (
        <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={onClose} data-testid="expense-modal">
            <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <div className="font-black text-lg">Attachments · {fmt(row.amount)}</div>
                    <button onClick={onClose} className="text-slate-400" data-testid="expense-modal-close"><X size={18}/></button>
                </div>
                <ExpenseFilesEditor row={row} canRemove={false} onChanged={onFilesChanged}/>
            </div>
        </div>
    );
    return <ExpenseForm {...{ mode, row, categories, isAdmin, onFilesChanged, onClose, onSaved }}/>;
}

function ExpenseForm({ mode, row, categories, isAdmin, onFilesChanged, onClose, onSaved }) {
    const isEdit = mode === "edit";
    const [form, setForm] = useState({
        expense_date: row?.expense_date || new Date().toISOString().slice(0, 10),
        amount: row?.amount ?? "",
        category: row?.category || "",
        narration: row?.narration || "",
        payment_mode: row?.payment_mode || "bank",
    });
    const [newCat, setNewCat] = useState(false);
    const [bill, setBill] = useState(null);
    const [shot, setShot] = useState(null);
    const [saving, setSaving] = useState(false);
    const billRef = useRef(null);
    const shotRef = useRef(null);

    const save = async () => {
        const amt = parseFloat(form.amount);
        if (!form.expense_date) return toast.error("Pick a date");
        if (!amt || amt <= 0) return toast.error("Amount must be greater than zero");
        const cat = newCat ? form.category : form.category;
        if (!cat.trim()) return toast.error("Pick or add a category");
        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/expenses/${row.id}`, {
                    expense_date: form.expense_date, amount: amt,
                    category: cat.trim(), narration: form.narration,
                    payment_mode: form.payment_mode,
                });
                toast.success("Expense updated");
            } else {
                const fd = new FormData();
                fd.append("expense_date", form.expense_date);
                fd.append("amount", String(amt));
                fd.append("category", cat.trim());
                fd.append("narration", form.narration || "");
                fd.append("payment_mode", form.payment_mode);
                if (bill) fd.append("bill", bill);
                if (shot) fd.append("screenshot", shot);
                await api.post("/expenses", fd);
                toast.success(isAdmin ? "Expense recorded" : "Expense submitted for approval");
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={onClose} data-testid="expense-modal">
            <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <div className="font-black text-lg text-slate-900">
                        {isEdit ? "Edit expense" : "Record an expense"}
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="expense-modal-close">
                        <X size={18}/>
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="hdb-label">Date</label>
                        <input type="date" className="hdb-input hdb-mono w-full" value={form.expense_date}
                               onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                               data-testid="expense-form-date"/>
                    </div>
                    <div>
                        <label className="hdb-label">Amount (₹)</label>
                        <input type="number" min="0" step="0.01" className="hdb-input hdb-mono w-full text-right"
                               value={form.amount}
                               onChange={(e) => setForm({ ...form, amount: e.target.value })}
                               placeholder="0.00" data-testid="expense-form-amount"/>
                    </div>
                </div>

                <div className="mt-3">
                    <label className="hdb-label">Category</label>
                    {!newCat ? (
                        <select value={categories.includes(form.category) ? form.category : ""}
                                onChange={(e) => {
                                    if (e.target.value === "__new__") { setNewCat(true); setForm({ ...form, category: "" }); }
                                    else setForm({ ...form, category: e.target.value });
                                }}
                                className="hdb-input w-full" data-testid="expense-form-category">
                            <option value="">Select category…</option>
                            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                            <option value="__new__">+ Add new category…</option>
                        </select>
                    ) : (
                        <div className="flex gap-2">
                            <input value={form.category} autoFocus
                                   onChange={(e) => setForm({ ...form, category: e.target.value })}
                                   placeholder="New category name (e.g. Staff Welfare)"
                                   className="hdb-input w-full" data-testid="expense-form-new-category"/>
                            <button onClick={() => setNewCat(false)} className="hdb-btn hdb-btn-ghost text-xs"
                                    data-testid="expense-form-cancel-new-cat">
                                Existing
                            </button>
                        </div>
                    )}
                </div>

                <div className="mt-3">
                    <label className="hdb-label">Narration</label>
                    <textarea value={form.narration} rows={2}
                              onChange={(e) => setForm({ ...form, narration: e.target.value })}
                              placeholder="What was this expense for?"
                              className="hdb-input w-full" data-testid="expense-form-narration"/>
                </div>

                <div className="mt-3">
                    <label className="hdb-label">Paid by</label>
                    <div className="flex gap-2" data-testid="expense-form-payment-mode">
                        {[["bank", "Bank / UPI / Card"], ["cash", "Cash (petty cash)"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setForm({ ...form, payment_mode: k })}
                                    data-testid={`expense-form-pay-${k}`}
                                    className={`hdb-btn text-xs flex-1 ${form.payment_mode === k ? "hdb-btn-primary" : "hdb-btn-ghost"}`}>
                                {l}
                            </button>
                        ))}
                    </div>
                </div>

                {!isEdit && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                        <div>
                            <label className="hdb-label">Bill / invoice (optional)</label>
                            <input ref={billRef} type="file" accept="image/*,application/pdf" className="hidden"
                                   onChange={(e) => setBill(e.target.files?.[0] || null)}
                                   data-testid="expense-form-bill-input"/>
                            <button onClick={() => billRef.current?.click()}
                                    className="hdb-btn hdb-btn-ghost w-full text-xs" data-testid="expense-form-bill-btn">
                                <Paperclip size={12}/> {bill ? bill.name : "Upload bill"}
                            </button>
                        </div>
                        <div>
                            <label className="hdb-label">Payment screenshot (optional)</label>
                            <input ref={shotRef} type="file" accept="image/*" className="hidden"
                                   onChange={(e) => setShot(e.target.files?.[0] || null)}
                                   data-testid="expense-form-shot-input"/>
                            <button onClick={() => shotRef.current?.click()}
                                    className="hdb-btn hdb-btn-ghost w-full text-xs" data-testid="expense-form-shot-btn">
                                <Image size={12}/> {shot ? shot.name : "Upload screenshot"}
                            </button>
                        </div>
                    </div>
                )}
                {isEdit && <ExpenseFilesEditor row={row} canRemove={isAdmin} onChanged={onFilesChanged}/>}
                {!isEdit && !isAdmin && (
                    <div className="mt-3 text-[11px] text-amber-700" data-testid="expense-form-pending-note">
                        This will be sent to an admin for approval before it counts in totals.
                    </div>
                )}

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="hdb-btn hdb-btn-ghost" data-testid="expense-form-cancel">
                        Cancel
                    </button>
                    <button onClick={save} disabled={saving} className="hdb-btn hdb-btn-primary"
                            data-testid="expense-form-save">
                        {saving ? <Loader2 className="animate-spin" size={13}/> : null}
                        {isEdit ? "Save changes" : "Save expense"}
                    </button>
                </div>
            </div>
        </div>
    );
}
